import * as Sentry from "@sentry/react";
import { useAppStore } from "../../stores/appStore";
import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import SVG from "react-inlinesvg";
import { useLocation } from "react-router-dom";
import Cookies from "universal-cookie";
import { APP_DOMAIN, PRICING_PLANS, TRIAL_CREDITS_TEST_KEY, DEFAULT_PRICING_VERSION } from "../../../../src/shared/constants.js";
import Auth from "../../Auth";
import { chooseRandom, sendAppEvent, useQuery } from "../../Utils";
import useChurnkey from "../../hooks/useChurnkey";
import useVatCheckoutGate from "../../hooks/useVatCheckoutGate";
import useWindowSize from "../../hooks/useWindowSize";
import { useVatCheckoutStore } from "../../stores/vatCheckoutStore";
import Subscription from "../../models/Subscription";
import { BillingAlert } from "../BillingAlert/index.jsx";
import Footer from "../Footer";
import FAQ from "./FAQ";
import FeedbackSection from "./FeedbackSection";
import MadeBy from "./MadeBy";
import PricingColumn from "./PricingColumn/";
import PricingModal from "./PricingModal";
import { shouldRequireAnnualOnly } from "./annualOnly";
import { DEV_ONBOARDING_SESSION_PRODUCT_ID, PRICING_MID_BREAKPOINT, PRICING_MOBILE_BREAKPOINT, PROD_ONBOARDING_SESSION_PRODUCT_ID } from "./constants";
import { StyledCancelGuarantee, StyledNav, StyledOneOnOneOnboarding, StyledPricing, StyledPricingColumnContainer } from "./styles";
import SudowriteLogo from "../SudowriteLogo";
import { isMobileOnly } from "react-device-detect";
import { requiresConferencePayment } from "../../../../src/shared/conferenceCampaign";

const STORE_LINKS = {
  app_store: {
    storeName: "App Store",
    manageUrl: "https://apps.apple.com/account/billing",
  },
  play_store: {
    storeName: "Google Play Store",
    manageUrl: "https://play.google.com/store/account/subscriptions",
  },
};

const INTENT_UPGRADE = "UPGRADE";
const INTENT_DOWNGRADE = "DOWNGRADE";
const INTENT_NO_CHANGE = "NO_CHANGE";

const getIntent = (currentPlan, targetPlan) => {
  if (!currentPlan || !targetPlan) {
    return null;
  }

  if (targetPlan.allotment > currentPlan.allotment) {
    return INTENT_UPGRADE;
  } else if (targetPlan.allotment < currentPlan.allotment) {
    return INTENT_DOWNGRADE;
  } else if (targetPlan.price > currentPlan.price) {
    return INTENT_UPGRADE;
  } else if (targetPlan.price < currentPlan.price) {
    return INTENT_DOWNGRADE;
  } else if (targetPlan.interval === "month" && currentPlan.interval === "year") {
    return INTENT_UPGRADE;
  } else if (targetPlan.interval === "year" && currentPlan.interval === "month") {
    return INTENT_DOWNGRADE;
  } else {
    return INTENT_NO_CHANGE;
  }
};

const Pricing = ({
  openLogin,
  showFront,
  hideFront,

  // optional little component used for the mobile version of the pricing page
  messageComponent,
}) => {
  const [ready, setReady] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentPlan, setCurrentPlan] = useState(false);
  const [nextPlan, setNextPlan] = useState(false);
  const [targetPlan, setTargetPlan] = useState(false);
  const [allPlans, setAllPlans] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const [isMobileView, setIsMobileView] = useState(false);
  const [isMidView, setIsMidView] = useState(false);

  const [outOfVersion, setOutOfVersion] = useState(false); // whether the user's current plan is not in the pricing version we're offering
  const [planInterval, setPlanInterval] = useState("year");
  const user = useAppStore((state) => state.user);
  const loadedUser = useAppStore((state) => state.loadedUser);
  const [forceShowingPlans, setForceShowingPlans] = useState(false); // whether to show the plans in transition

  const [error, setError] = useState(false);

  const size = useWindowSize();

  const query = useQuery();
  const fromMobileApp = query.get("source") === "mobile";

  // SW-8538 Phase B: EU users who pick a tier on /app/plans must collect
  // VAT info before checkout. We open the VAT modal directly via the
  // Zustand store with `subscription.changePlan(...)` as the onComplete
  // continuation. Non-EU users skip the modal entirely.
  const {
    showVatStep,
    country: vatCountry,
    classification: vatClassificationKind,
    isLoading: vatGateLoading,
  } = useVatCheckoutGate();
  const openVatModal = useVatCheckoutStore((state) => state.openVatModal);

  const location = useLocation();
  const eventSignup = location.pathname === "/event/get-in/";

  const conferencePaymentRequired = requiresConferencePayment(subscription?.data);
  const hasFreeTrial = !conferencePaymentRequired && subscription && subscription.activeTrial();
  const hasFreePlan = !conferencePaymentRequired && ((subscription && currentPlan?.price === 0) || (subscription && nextPlan?.price === 0));
  const requiresAnnualOnly = shouldRequireAnnualOnly({
    user,
    annualParam: query.get("annual"),
    annualCookie: new Cookies().get("annual"),
    subscription,
  });
  const isRevcat = subscription && subscription.isRevenueCatSubscription;
  const subscriptionSource = subscription?.data?.subscriptionSource;
  const isStoreSubscriber = subscription && subscription.activeSubscription() && ["app_store", "play_store"].includes(subscriptionSource || "");
  const storeInfo = useMemo(() => {
    if (!isStoreSubscriber) return null;
    return STORE_LINKS[subscriptionSource] || null;
  }, [isStoreSubscriber, subscriptionSource]);
  const isMobileDevice = isMobileOnly;
  const disableStoreActions = isStoreSubscriber && !isMobileDevice;

  const pricingVersion = null;

  const onboardingSessionId = process.env.NODE_ENV === "production" ? PROD_ONBOARDING_SESSION_PRODUCT_ID : DEV_ONBOARDING_SESSION_PRODUCT_ID;

  // used for AB testing purposes - no current test running
  // to implement test see: https://github.com/jamesyu/sudowrite-express/pull/3156
  function runPricingTest() {
    const cookies = new Cookies();
    // check cookies
    let id = cookies.get(TRIAL_CREDITS_TEST_KEY);

    if (!id) {
      id = chooseRandom(["4.1", "4.2"]);

      cookies.set(TRIAL_CREDITS_TEST_KEY, id, {
        maxAge: 60 * 60 * 24 * 365 * 4,
        path: "/",
      });
    }

    return id;
  }

  useEffect(() => {
    showFront();

    return () => {
      hideFront();
    };
  }, []);

  const ref = query.get("ref");

  useEffect(() => {
    // App bootstrap resolves impersonation before publishing the customer.
    // A raw Firebase listener would read subscriptions/<synthetic session UID>.
    let cancelled = false;
    setReady(false);
    setSubscription(null);
    setAllPlans([]);
    setCurrentPlan(false);
    setNextPlan(false);
    setOutOfVersion(false);
    setError(false);
    // A null user during restoration/validation is not a confirmed sign-out.
    if (!loadedUser) return;
    if (user && !user.isAnonymous) {
      Subscription.fetchFromUserId(user.uid).then((sub) => {
        if (cancelled) return;
        if (!sub) {
          Sentry.captureMessage("User exists in client but not in db on pricing page", {
            tags: {
              user: user?.uid,
              userEmail: user?.email,
            },
          });
          return;
        }

        setSubscription(sub);
        setAllPlans(sub.plansToOffer());
        setCurrentPlan(sub.currentPlan());
        setReady(true);

        // check if there is year or not
        const defaultInterval = sub.plansToOffer().some((plan) => plan.interval === "year") ? "year" : "month";

        setPlanInterval(sub.currentPlan()?.interval || defaultInterval); // Switch to show the interval which is the current plan
        setNextPlan(sub.nextPlan());

        if (sub.currentPlanIsLegacy()) {
          // the user's current plan's major version isn't in the same major version offered
          // setPlanInterval("year");
          setOutOfVersion(true);
        }

        const currentPlan = sub.currentPlan();
        const nextPlan = sub.nextPlan();

        sendAppEvent("Pricing View - Logged In", {
          subscription_current_plan_id: currentPlan?.id,
          subscription_next_plan_id: nextPlan?.id,
          ref: ref,
          cancel: query.get("cancel"),
        });
      }).catch((error) => {
        if (cancelled) return;
        Sentry.captureException(error);
        setError({ message: "Unable to load your plans. Please refresh and try again." });
      });
    } else {
      setReady(true);
      // logged out
      setSubscription(null);

      // when running AB pricing tests, assign version with the following line
      // const version = runPricingTest();
      const version = DEFAULT_PRICING_VERSION;

      setAllPlans(PRICING_PLANS[version].plans.filter((plan) => !plan.hidden));

      sendAppEvent("Pricing View - Logged Out", {
        ref: ref,
      });
    }
    return () => { cancelled = true; };
  }, [user, loadedUser]);

  useEffect(() => {
    setIsMobileView(size.width < parseInt(PRICING_MOBILE_BREAKPOINT, 10));
    setIsMidView(size.width >= parseInt(PRICING_MOBILE_BREAKPOINT, 10) && size.width < parseInt(PRICING_MID_BREAKPOINT, 10));
  }, [size, isMobileView, isMidView]);

  const [churnkeyLoaded, churnkeyToken] = useChurnkey({ subscription });

  useEffect(() => {
    if (!isStoreSubscriber || !storeInfo) return;

    if (!isMobileDevice) {
      setError({
        type: "mobile_subscription_active",
        message: `You subscribed to Sudowrite via the ${storeInfo.storeName}. Please manage your subscription through the ${storeInfo.storeName}.`,
        store: subscriptionSource,
        storeName: storeInfo.storeName,
      });
    } else if (error && error.type === "mobile_subscription_active") {
      setError(false);
    }
  }, [isStoreSubscriber, storeInfo, isMobileDevice, subscriptionSource, error]);

  if (!loadedUser) return <StyledPricing notLoaded={true} />;
  if (allPlans.length === 0) return <StyledPricing notLoaded={true}>{error?.message && <p role="alert">{error.message}</p>}</StyledPricing>;

  const hasAnnualPlans = !!allPlans.find((plan) => plan.interval === "year");

  let legacyPlan = null;
  if (currentPlan && outOfVersion) {
    legacyPlan = currentPlan;
  }

  let nextV4Plan = null;
  // Check the case where they have a scheduled transition from a v3 plan to a v4 plan
  if (((currentPlan && currentPlan.version[0] !== "4") || !currentPlan || (!currentPlan && nextPlan?.version[0] === "4")) && nextPlan && nextPlan.version[0] === "4") {
    nextV4Plan = nextPlan;
  }

  // Filter to plans which we show
  let plans;
  let V4TransitionView = false;

  if (nextV4Plan && !forceShowingPlans) {
    plans = [];
    V4TransitionView = true;
  } else {
    plans = allPlans.filter((plan) => plan.interval === (requiresAnnualOnly ? "year" : planInterval));
  }

  const handleSelectPlan = async (subscription, plan) => {
    if (eventSignup) {
      if (!submitting) setSubmitting(true);
      Subscription.anonymousSubscription({
        stripePlanId: plan.id,
      });
      return;
    }

    if (!user) {
      if (requiresAnnualOnly) {
        window.location.href = process.env.NODE_ENV === "production" ? `https://${APP_DOMAIN}/app?sub_first=true&annual=true` : "/app?sub_first=true&annual=true";
        return;
      }

      openLogin();
      return;
    }

    if (subscription.activeSubscription() && subscription.data?.subscriptionSource && ["app_store", "play_store"].includes(subscription.data.subscriptionSource)) {
      const targetStoreInfo = STORE_LINKS[subscription.data.subscriptionSource];

      if (isMobileDevice && targetStoreInfo?.manageUrl) {
        window.location.href = targetStoreInfo.manageUrl;
      } else {
        setError({
          type: "mobile_subscription_active",
          message: `You subscribed to Sudowrite via the ${targetStoreInfo?.storeName || "mobile app"}. Please manage your subscription through the ${targetStoreInfo?.storeName || "mobile app"}.`,
          store: subscription.data.subscriptionSource,
          manageLink: targetStoreInfo?.manageUrl,
          storeName: targetStoreInfo?.storeName,
        });
      }
      return;
    }

    const currentPlan = subscription.currentPlan();
    const nextPlan = subscription.nextPlan();

    let intent = getIntent(currentPlan, plan);

    sendAppEvent("Pricing - Plan Selected", {
      plan_title: plan.title,
      plan_price: plan.price,
      plan_interval: plan.interval,
      plan_version: plan.version,
      plan_id: plan.id,
      plan_packset: plan.packSet,
      subscription_current_plan_id: currentPlan?.id,
      subscription_next_plan_id: nextPlan?.id,
      intent,
      ref: query.get("ref"),
    });

    // If the subscription is canceled and the user is renewing the same plan, skip the modal
    // and call changePlan directly (this sets resubscribe_intent metadata so the webhook allows it)
    if (subscription.canceled() && subscription.currentPlan()?.id === plan.id) {
      if (submitting) return;
      setSubmitting(true);
      subscription
        .changePlan({ stripePlanId: plan.id })
        .then((result) => {
          if (result && result.error) {
            setSubmitting(false);
            alert("Something went wrong: " + result.message);
          } else {
            window.location.reload();
          }
        })
        .catch(() => {
          setSubmitting(false);
          setError(true);
        });
      return;
    }

    if (subscription.currentPlan() && !modalOpen) {
      setTargetPlan(plan);
      setModalOpen(true);
      return;
    }

    // SW-8538 Phase B: VAT collection runs only for users who have never
    // paid before — they're the cohort that requires it. Existing paid
    // users are grandfathered with `tax_exempt: "exempt"` (applied by
    // scripts/apply_existing_eu_vat_exemption.js) until we email them,
    // so we deliberately let them go through changePlan → Stripe directly
    // without the VAT modal. Free-plan-to-paid users count as new
    // (hasPaid=false) and DO need the modal.
    //
    // While geo is still loading, suppress the click — the user's next
    // click after settle will route correctly. Falling through to
    // changePlan here would silently bypass VAT for an EU user we
    // haven't classified yet.
    if (plan && !subscription.hasPaid) {
      if (vatGateLoading) return;
      if (showVatStep) {
        // Synchronous re-entry guard: if openVatModal was already called
        // this tick (rapid double-click before React re-renders) the store
        // already has isOpen=true. Without this, a second click would
        // overwrite the first onComplete closure and silently lose the
        // original plan selection.
        if (useVatCheckoutStore.getState().isOpen) return;
        const sentryExtra = {
          planId: plan.id,
          planInterval: plan.interval,
          planTitle: plan.title,
          country: vatCountry,
          classification: vatClassificationKind,
        };
        openVatModal({
          onComplete: () =>
            subscription
              .changePlan({ stripePlanId: plan.id })
              .then((result) => {
                if (result && result.error) {
                  Sentry.captureException(
                    new Error(`changePlan returned error: ${result.message}`),
                    {
                      tags: { feature: "vat.pricing.changePlan" },
                      extra: sentryExtra,
                    }
                  );
                  alert("Something went wrong: " + result.message);
                } else if (result && result.success) {
                  if (result.stripePlanId && result.stripePlanId === plan.id) {
                    window.location = "/app/checkout?sscid=" + plan.id;
                  }
                }
              })
              .catch((err) => {
                Sentry.captureException(err, {
                  tags: { feature: "vat.pricing.changePlan" },
                  extra: sentryExtra,
                });
                setError(true);
              }),
          planContext: { id: plan.id, interval: plan.interval, title: plan.title },
          origin: "pricing",
        });
        return;
      }
    }

    if (submitting) return;
    if (!submitting) setSubmitting(true);

    if (plan) {
      subscription
        .changePlan({
          stripePlanId: plan.id,
        })
        .then((result) => {
          if (result && result.error) {
            alert("Something went wrong: " + result.message);
          } else if (result && result.success) {
            if (result.stripePlanId && result.stripePlanId === plan.id) {
              window.location = "/app/checkout?sscid=" + plan.id;
            }
          }
        })
        .catch(() => {
          setSubmitting(false);
          setError(true);
        });
    } else {
      // User wants to cancel their subscription
      const currentPlan = subscription.currentPlan();
      sendAppEvent("Wants to cancel", {
        current_plan_id: currentPlan?.id,
        current_plan_title: currentPlan?.title,
        current_plan_price: currentPlan?.price,
        ref: query.get("ref"),
      });

      subscription
        .changePlan({
          stripePlanId: null,
          cancel: true,
        })
        .then((result) => {
          if (result && result.error) {
            alert("Something went wrong: " + result.message);
          } else if (result && result.success) {
            window.location = "/app/checkout?cancel=1";
          }
        })
        .catch(() => {
          setSubmitting(false);
          setError(true);
        });
    }
  };

  if (!ready) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>Pricing</title>
      </Helmet>

      {/* This Pricing Modal is for switching plans */}
      {modalOpen && (
        <PricingModal
          setModalOpen={setModalOpen}
          currentPlan={currentPlan}
          targetPlan={targetPlan}
          subscription={subscription}
          handleSelectPlan={handleSelectPlan}
          hasFreePlan={hasFreePlan}
          hasFreeTrial={hasFreeTrial}
          submitting={submitting}
        />
      )}

      <StyledPricing
        modalOpen={modalOpen}
        tabIndex="0"
      >
        {fromMobileApp ? (
          <div style={{ textAlign: "center", padding: "16px 20px", borderBottom: "1px solid #eee" }}>
            <a
              href="sudowrite://"
              style={{ color: "#6B46C1", textDecoration: "none", fontWeight: 500, fontSize: "14px" }}
            >
              &larr; Return to Sudowrite App
            </a>
          </div>
        ) : (
          <StyledNav>
            <SudowriteLogo
              isLink
              size="large"
              opacity={0.5}
              style={{ paddingLeft: "24px" }}
            />

            <ul>
              <li>
                <a>Pricing</a>
              </li>
              {!isMobileView && !user && openLogin && (
                <li>
                  <a
                    onClick={(e) => {
                      openLogin();
                    }}
                  >
                    Log in
                  </a>
                </li>
              )}

              {user && (
                <li>
                  <a
                    onClick={(e) => {
                      e.preventDefault();

                      Auth.markLogoutInProgress();
                      window.location.href = "/logout";
                    }}
                  >
                    Log out
                  </a>
                </li>
              )}
            </ul>
          </StyledNav>
        )}

        <section>
          {messageComponent}
          {conferencePaymentRequired && <div style={{ maxWidth: 560, margin: "20px auto 28px", padding: "0 22px", textAlign: "center" }} role="status">
            <h1 style={{ fontSize: 34, lineHeight: 1.15, margin: "0 0 14px" }}>Choose your writing plan</h1>
            <p style={{ font: "16px/1.5 Inter, sans-serif", margin: 0 }}>Welcome from Author Nation. Choose a plan and start your next chapter.</p>
          </div>}
          {currentPlan && !outOfVersion && !conferencePaymentRequired && <h1 className="manage">Manage Your Account</h1>}
          {currentPlan && outOfVersion && !isMobileView && !V4TransitionView && !nextV4Plan && (
            <img
              className="our-new-plans"
              src="/assets/our-new-plans.png"
            />
          )}
          {currentPlan && outOfVersion && isMobileView && !V4TransitionView && !nextV4Plan && (
            <img
              className="our-new-plans"
              src="/assets/our-new-plans-mobile.png"
            />
          )}
          {!currentPlan && !conferencePaymentRequired && (
            <>
              <h1>Say Goodbye to Writer's Block</h1>
              <span className="pricing-subhead">
                Use the #1 Creative Writing AI
                <br />
                and make writing fun again.
              </span>
            </>
          )}
          {nextV4Plan && (
            <div className="transition-header">
              <h2>We're Moving You to a Better Plan</h2>

              <p>
                Sudowrite is switching to credits! Prices aren't changing, but we'll be moving off of "AI Words". Your new plan will be a better value overall.{" "}
                <a
                  href="https://docs.sudowrite.com/plans--account/wBnmhtSyMcWtk2BLzifGkz/what-are-credits/qzSpgs9UDPEtwYcdczkLd3"
                  target="_blank"
                >
                  Read more about it here
                </a>
                .
              </p>
            </div>
          )}

          {currentPlan && isStoreSubscriber && storeInfo && (
            <div className="transition-header mt-4">
              <p>
                You subscribed to Sudowrite via the {storeInfo.storeName}. Manage your subscription through the {storeInfo.storeName}.
                {isMobileDevice && storeInfo.manageUrl && (
                  <>
                    {" "}
                    <a href={storeInfo.manageUrl}>Open the {storeInfo.storeName}</a>
                  </>
                )}
                {!isMobileDevice && " Please open the mobile app to make changes."}
              </p>
            </div>
          )}
          {hasAnnualPlans && !requiresAnnualOnly && !V4TransitionView && (
            <div className="interval-switch">
              <div>Monthly</div>
              <label className="switch">
                <input
                  type="checkbox"
                  id="wordCount"
                  aria-label="Bill annually"
                  checked={planInterval === "year"}
                  onChange={(e) => {
                    sendAppEvent("Pricing - Interval Changed", {
                      interval: e.target.checked ? "year" : "month",
                      subscription_current_plan_id: currentPlan?.id,
                      subscription_next_plan_id: nextPlan?.id,
                      ref: query.get("ref"),
                    });
                    setPlanInterval(e.target.checked ? "year" : "month");
                  }}
                />
                <span className="slider round"></span>
              </label>
              <div className="year">
                Yearly
                <span>Up to 50% off</span>
              </div>
            </div>
          )}
          {conferencePaymentRequired && <p style={{ textAlign: "center", font: "14px/1.5 Inter, sans-serif", margin: "18px 20px 22px" }}>{planInterval === "year" ? "Annual plans are billed once a year. Prices below show the monthly equivalent." : "Monthly plans are billed each month."}</p>}
          {isMidView && (legacyPlan || nextV4Plan) && (
            <div className="legacy-mid-container">
              {legacyPlan && (
                <PricingColumn
                  isRevcatSubscription={isRevcat}
                  storeInfo={storeInfo}
                  isStoreSubscriber={isStoreSubscriber}
                  disableStoreActions={disableStoreActions}
                  churnkeyToken={churnkeyToken}
                  isMobileDevice={isMobileDevice}
                  key={legacyPlan.id}
                  plan={legacyPlan}
                  subscription={subscription}
                  handleSelectPlan={handleSelectPlan}
                  currentPlan={legacyPlan}
                  hasFreePlan={hasFreePlan}
                  hasFreeTrial={hasFreeTrial}
                  isMobileView={isMobileView}
                  position="comparison"
                  legacy={outOfVersion}
                  user={user}
                />
              )}

              {nextV4Plan && (
                <PricingColumn
                  isRevcatSubscription={isRevcat}
                  storeInfo={storeInfo}
                  isStoreSubscriber={isStoreSubscriber}
                  disableStoreActions={disableStoreActions}
                  churnkeyToken={churnkeyToken}
                  isMobileDevice={isMobileDevice}
                  key={nextV4Plan.id}
                  plan={nextV4Plan}
                  subscription={subscription}
                  handleSelectPlan={handleSelectPlan}
                  currentPlan={currentPlan}
                  hasFreePlan={hasFreePlan}
                  hasFreeTrial={hasFreeTrial}
                  isMobileView={isMobileView}
                  isShadowPlanTransition
                  position="comparison"
                  user={user}
                />
              )}

              <div className="scroll-down">
                Scroll down to see
                <br />
                our new plans
                <span>Up to 50% off</span>
                <SVG src="/assets/pricing-more-arrow.svg" />
              </div>
            </div>
          )}
          <StyledPricingColumnContainer hasFreePlan={hasFreePlan}>
            {!isMidView && (legacyPlan || nextV4Plan) && (
              <>
                {legacyPlan && (
                  <PricingColumn
                    isRevcatSubscription={isRevcat}
                    storeInfo={storeInfo}
                    isStoreSubscriber={isStoreSubscriber}
                    disableStoreActions={disableStoreActions}
                    churnkeyToken={churnkeyToken}
                    isMobileDevice={isMobileDevice}
                    key={legacyPlan.id}
                    plan={legacyPlan}
                    subscription={subscription}
                    handleSelectPlan={handleSelectPlan}
                    currentPlan={currentPlan}
                    hasFreePlan={hasFreePlan}
                    hasFreeTrial={hasFreeTrial}
                    isMobileView={isMobileView}
                    position="comparison"
                    legacy={outOfVersion}
                    user={user}
                  />
                )}

                {nextV4Plan && (
                  <PricingColumn
                    isRevcatSubscription={isRevcat}
                    storeInfo={storeInfo}
                    isStoreSubscriber={isStoreSubscriber}
                    disableStoreActions={disableStoreActions}
                    churnkeyToken={churnkeyToken}
                    isMobileDevice={isMobileDevice}
                    key={nextV4Plan.id}
                    plan={nextV4Plan}
                    subscription={subscription}
                    handleSelectPlan={handleSelectPlan}
                    currentPlan={currentPlan}
                    hasFreePlan={hasFreePlan}
                    hasFreeTrial={hasFreeTrial}
                    isMobileView={isMobileView}
                    isShadowPlanTransition
                    position="comparison"
                    user={user}
                  />
                )}
              </>
            )}

            {plans &&
              plans
                .filter((plan) => !(plan.price === 0 && !hasFreePlan))
                .map((plan, i) => {
                  return (
                    <PricingColumn
                      isRevcatSubscription={isRevcat}
                      storeInfo={storeInfo}
                      isStoreSubscriber={isStoreSubscriber}
                      disableStoreActions={disableStoreActions}
                      churnkeyToken={churnkeyToken}
                      isMobileDevice={isMobileDevice}
                      key={plan.id}
                      plan={plan}
                      subscription={subscription}
                      handleSelectPlan={handleSelectPlan}
                      currentPlan={currentPlan}
                      hasFreePlan={hasFreePlan}
                      hasFreeTrial={hasFreeTrial}
                      isMobileView={isMobileView}
                      position={i}
                      submitting={submitting}
                      user={user}
                    />
                  );
                })}

            {!forceShowingPlans && nextV4Plan && (
              <div className="show-more">
                <button
                  onClick={(e) => {
                    setForceShowingPlans(true);
                  }}
                >
                  <div>
                    Show all
                    <br />
                    new plans
                  </div>
                  <div>&rarr;</div>
                </button>
              </div>
            )}
          </StyledPricingColumnContainer>
          {subscription?.data?.features?.requiresSubscriptionFirst && <p className="sub-first-refund-notice">Not sure yet? Cancel within your first week for a full refund.</p>}
          {<BillingAlert error={error} />}
          <StyledCancelGuarantee>
            <SVG src={"/assets/pricing/ez-guarantee-symbol.svg"} />
            <h3>EZ Cancel Guarantee</h3>
            <p>
              We want you to use Sudowrite because you <em>love</em> it, not because it's annoying to cancel.
            </p>
            <p>
              So we make it <em>very</em> easy to upgrade, downgrade, take a break, try it again, or cancel forever.
            </p>
            <p>It's your choice.</p>
          </StyledCancelGuarantee>
          {!eventSignup && !conferencePaymentRequired && (
            <StyledOneOnOneOnboarding>
              <div className="container">
                <h3>30-Minute Personal Setup Session</h3>
                <div className="price">$49</div>
                <p>Enjoy a private one-on-one session with Sudowrite staff. Tailored to your needs to help you get up to speed quickly.</p>
                <button
                  onClick={() => {
                    window.open(`https://buy.stripe.com/${onboardingSessionId}?client_reference_id=${user.uid}&prefilled_email=${user.email}`, "_blank");
                  }}
                >
                  Purchase
                </button>
              </div>
            </StyledOneOnOneOnboarding>
          )}
        </section>

        {!fromMobileApp && !conferencePaymentRequired && (
          <>
            <MadeBy
              targetPlan={plans[0]}
              subscription={subscription}
              handleSelectPlan={handleSelectPlan}
            />

            <FeedbackSection />

            <FAQ questions="pricing" />

            <Footer
              theme="light"
              openLogin={openLogin}
              user={user}
            />
          </>
        )}
      </StyledPricing>
    </>
  );
};

export default Pricing;
